import { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import BlogLayout from '../blog/BlogLayout';
import PageSidebar from '../blog/PageSidebar';
import MetaTags from '../common/MetaTags';

export default function StaticPageView({ slug, onNavigate }) {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  async function loadPage() {
    setLoading(true);
    setError(false);
    try {
      const q = query(
        collection(db, 'pages'),
        where('slug', '==', slug),
        where('published', '==', true)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError(true);
      } else {
        setPage({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() });
      }
    } catch (err) {
      console.error('Error loading page:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <BlogLayout showSidebar={false}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: 'var(--muted)' }}>
            Loading...
          </div>
        </div>
      </BlogLayout>
    );
  }

  if (error || !page) {
    return (
      <BlogLayout showSidebar={false}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
          <h1 style={{ fontSize: '32px', marginBottom: '16px' }}>Page Not Found</h1>
          <p style={{ color: 'var(--muted)', marginBottom: '24px' }}>
            The page you're looking for doesn't exist.
          </p>
          <button
            onClick={() => onNavigate('landing')}
            className="btn btn-primary"
          >
            ← Back to Home
          </button>
        </div>
      </BlogLayout>
    );
  }

  // Helper function to strip HTML tags
  function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  // Generate clean description from content
  const metaDescription = stripHtml(page.content).substring(0, 160);

  return (
    <BlogLayout showSidebar={true} sidebarComponent={<PageSidebar />}>
      <MetaTags
        title={page.title}
        description={metaDescription}
        image={page.featuredImage}
        url={window.location.href}
        type="website"
      />
      <style>{`
        .static-page-article {
          max-width: 920px;
          margin: 0 auto;
          padding: 40px 20px;
        }

        .static-page-h1 {
          font-size: 48px;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 32px;
          color: #1a1a1a;
        }

        .static-page-featured-image {
          width: 100%;
          max-height: 500px;
          border-radius: 12px;
          overflow: hidden;
          margin-bottom: 40px;
        }

        .static-page-featured-image img {
          width: 100%;
          height: auto;
          display: block;
          object-fit: cover;
        }

        .static-page-content {
          color: #333;
        }

        /* Only apply typography to paragraphs without custom styling */
        .static-page-content > p:not([style]):not([class]) {
          font-size: 18px;
          line-height: 1.8;
        }

        /* Responsive styles */
        @media (max-width: 768px) {
          .static-page-article {
            padding: 24px 16px;
          }

          .static-page-h1 {
            font-size: 32px;
            margin-bottom: 24px;
          }

          .static-page-featured-image {
            max-height: 300px;
            border-radius: 8px;
            margin-bottom: 24px;
          }

          .static-page-content > p:not([style]):not([class]) {
            font-size: 16px;
            line-height: 1.7;
          }
        }

        @media (max-width: 480px) {
          .static-page-h1 {
            font-size: 28px;
          }

          .static-page-content > p:not([style]):not([class]) {
            font-size: 15px;
          }
        }
      `}</style>
      <article className="static-page-article">
        {/* Title */}
        <h1 className="static-page-h1">
          {page.title}
        </h1>

        {/* Featured Image */}
        {page.featuredImage && (
          <div className="static-page-featured-image">
            <img
              src={page.featuredImage}
              alt={page.title}
            />
          </div>
        )}

        {/* Content */}
        <div
          className="static-page-content blog-content"
          dangerouslySetInnerHTML={{ __html: page.content }}
        />
      </article>
    </BlogLayout>
  );
}
